import React from 'react';

import { gql } from '@soundxyz/gql-string';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { SongCard } from '@components/basics/SongCard';
import { DateTimeTooltip } from '@components/main-components/datetime-tooltip';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import type { CollectorCardUserInfoFragmentDoc, FragmentType } from '@graphql/reactQuery';
import { CollectorCardFragmentDoc, getFragment } from '@graphql/reactQuery';
import { useSubscribeRelease } from '@hooks/useSubscribeRelease';
import { setUpNextTrackIds } from '@store/audio/AudioQueue';
import { SPACE } from '@utils/constants';
import { ReleaseCardActions } from '../ReleaseCardActions';
import { getCountdownString, shouldShowEndsInCountdown } from '../ReleaseInfoCard';
import { useReleaseCardInfo } from '../useReleaseCardInfo';
import { CollectorUser } from './CollectorInfo';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment CollectorCard on Release {
    id
    supplyCutoffTimestamp
    quantityUpperBound
    quantityLowerBound
    mintStartTimestamp
    ...ReleasesCardInfo
    ...ReleaseCardActions
    ...ReleaseUpdates
  }
`);

const SaleCountdown = ({ endTimeMs }: { endTimeMs: number }) => {
  return (
    <DateTimeTooltip
      dateTime={new Date(endTimeMs)}
      className="ml-auto mr-2 mt-3 flex w-fit items-center overflow-hidden rounded-full bg-black/30 px-2 py-1 text-[10px] font-semibold leading-[14px] text-white backdrop-blur-lg sm:text-base-s"
    >
      <div>
        <span>Ends in{SPACE}</span>
        <span>{getCountdownString({ timeStamp: endTimeMs })}</span>
      </div>
    </DateTimeTooltip>
  );
};
export const CollectorCard = ({
  releaseFrag,
  collectorFrag,
  editionsMinted,
  getNextTrackIds,
}: {
  releaseFrag: FragmentType<CollectorCardFragmentDoc>;
  collectorFrag: FragmentType<CollectorCardUserInfoFragmentDoc>;
  editionsMinted: number;
  getNextTrackIds: () => string[];
}) => {
  const release = getFragment(CollectorCardFragmentDoc, releaseFrag);
  const {
    coverImageUrl,
    trackUrl,
    onPlayClick,
    isAudioBuffering,
    playButtonPrefetchRef,
    isActiveTrack,
    isAlbum,
    hasAudioAccess,
    artistName,
    artistWebappPath,
    releaseTitle,
  } = useReleaseCardInfo({ data: release });

  const {
    data: { finalQuantity },
  } = useSubscribeRelease({
    release,
  });

  const { mintStartTimestamp, quantityUpperBound, quantityLowerBound, supplyCutoffTimestamp } =
    release;

  const showCountdown = shouldShowEndsInCountdown({
    finalQuantity,
    quantityUpperBound,
    quantityLowerBound,
    mintStartTimestamp,
  });

  const onPlayButtonClick = useStableCallback(async () => {
    await onPlayClick();
    setUpNextTrackIds(getNextTrackIds());
  });

  return (
    <div className="relative">
      <SongCard
        album={isAlbum}
        isPlaying={isActiveTrack}
        loading={false}
        edition={false}
        expanded={null}
        setExpanded={null}
      >
        <SongCard.Cover
          header={showCountdown && <SaleCountdown endTimeMs={supplyCutoffTimestamp ?? 0} />}
          coverLink={trackUrl}
          image={<SongCard.Cover.Single src={coverImageUrl} />}
          onPlayButtonClick={onPlayButtonClick}
          isBufferingSong={isAudioBuffering}
          disablePlay={!hasAudioAccess}
          playButtonPrefetchRef={playButtonPrefetchRef}
        />
        <SongCard.Info
          header={
            <AccessibleLink absoluteUri={trackUrl} type="external">
              {releaseTitle}
            </AccessibleLink>
          }
          subheader={<AccessibleLink pathnameUri={artistWebappPath}>{artistName}</AccessibleLink>}
          actions={<ReleaseCardActions data={release} />}
          bottom={
            <CollectorUser collectorUserFrag={collectorFrag} editionsMinted={editionsMinted} />
          }
        />
      </SongCard>
    </div>
  );
};
